"use client";

import * as React from "react";
import { CalendarIcon, Clock } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const DATE_VALUE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const TIME_VALUE_PATTERN = /^\d{2}:\d{2}(?::\d{2})?$/;

type DateTimePickerProps = {
	id?: string;
	value?: string;
	onChange: (value: string) => void;
	disabled?: boolean;
	className?: string;
	datePlaceholder?: string;
	timeLabel?: string;
	defaultTime?: string;
	"aria-describedby"?: string;
	"aria-invalid"?: boolean;
	"aria-label"?: string;
};

type DateTimeParts = {
	datePart: string;
	timePart: string;
};

function normalizeTimeValue(value: string | undefined): string {
	const normalized = String(value ?? "").trim();
	if (!normalized) return "";

	const match = normalized.match(/^(\d{2}:\d{2})(?::\d{2})?$/);
	return match?.[1] ?? "";
}

function splitDateTimeValue(value: string | undefined): DateTimeParts {
	const normalized = String(value ?? "").trim();
	if (!normalized) {
		return { datePart: "", timePart: "" };
	}

	const [rawDatePart = "", rawTimePart = ""] = normalized.split("T");
	return {
		datePart: DATE_VALUE_PATTERN.test(rawDatePart) ? rawDatePart : "",
		timePart: TIME_VALUE_PATTERN.test(rawTimePart) ? rawTimePart : "",
	};
}

function buildDateTimeValue(parts: DateTimeParts): string {
	if (!parts.datePart) return "";
	if (!parts.timePart) return `${parts.datePart}T`;
	return `${parts.datePart}T${parts.timePart}`;
}

function toDateInputValue(date: Date | undefined): string {
	if (!date || Number.isNaN(date.getTime())) return "";
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
}

function formatDisplayLabel(datePart: string, timePart: string, placeholder: string) {
	if (!datePart) return placeholder;

	const dateObj = new Date(`${datePart}T${timePart || "00:00:00"}`);
	if (Number.isNaN(dateObj.getTime())) return placeholder;

	const dateLabel = dateObj.toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
	});
	const timeLabel = dateObj.toLocaleTimeString("en-US", {
		hour: "numeric",
		minute: "2-digit",
	});

	return `${dateLabel} · ${timeLabel}`;
}

export function DateTimePicker({
	id,
	value,
	onChange,
	disabled,
	className,
	datePlaceholder = "Pick a date",
	timeLabel = "Check-in time",
	defaultTime = "10:30:00",
	...buttonProps
}: DateTimePickerProps) {
	const [open, setOpen] = React.useState(false);
	const timeInputRef = React.useRef<HTMLInputElement>(null);
	const generatedTimeInputId = React.useId();
	const timeInputId = id ? `${id}-time` : generatedTimeInputId;

	const parts = React.useMemo(() => splitDateTimeValue(value), [value]);
	const normalizedTimePart = React.useMemo(
		() => normalizeTimeValue(parts.timePart),
		[parts.timePart],
	);
	const normalizedDefaultTime = React.useMemo(
		() => normalizeTimeValue(defaultTime) || "10:30",
		[defaultTime],
	);

	const selectedDate = React.useMemo(
		() => (parts.datePart ? new Date(`${parts.datePart}T00:00:00`) : undefined),
		[parts.datePart],
	);

	// Sync time input when value prop changes externally (e.g., "Set Now" button)
	React.useEffect(() => {
		if (timeInputRef.current) {
			const newTimeValue = normalizedTimePart || normalizedDefaultTime;
			if (timeInputRef.current.value !== newTimeValue) {
				timeInputRef.current.value = newTimeValue;
			}
		}
	}, [normalizedTimePart, normalizedDefaultTime]);

	// Handle date selection from calendar
	const handleDateSelect = React.useCallback(
		(nextDate: Date | undefined) => {
			const datePart = toDateInputValue(nextDate);
			// Get current time value from the input ref, or use existing time, or default
			const timeValue =
				normalizeTimeValue(timeInputRef.current?.value) ||
				normalizedTimePart ||
				normalizedDefaultTime;
			onChange(buildDateTimeValue({ datePart, timePart: timeValue }));
			if (nextDate) setOpen(false);
		},
		[onChange, normalizedTimePart, normalizedDefaultTime],
	);

	// Handle time input changes
	const handleTimeChange = React.useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			if (!parts.datePart) return;
			const timePart = normalizeTimeValue(event.target.value);
			onChange(buildDateTimeValue({ datePart: parts.datePart, timePart }));
		},
		[onChange, parts.datePart],
	);

	// Format display label
	const displayLabel = React.useMemo(() => {
		return formatDisplayLabel(parts.datePart, parts.timePart, datePlaceholder);
	}, [parts.datePart, parts.timePart, datePlaceholder]);

	return (
		<div className="flex flex-col gap-2">
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger asChild>
					<Button
						id={id}
						type="button"
						variant="outline"
						disabled={disabled}
						className={cn(
							"w-full justify-start text-left font-normal",
							!parts.datePart && "text-muted-foreground",
							className,
						)}
						{...buttonProps}
					>
						<CalendarIcon className="mr-2 h-4 w-4" />
						{displayLabel}
					</Button>
				</PopoverTrigger>
				<PopoverContent className="w-auto p-0" align="start">
					<Calendar
						mode="single"
						selected={selectedDate}
						onSelect={handleDateSelect}
						defaultMonth={selectedDate ?? new Date()}
						initialFocus
					/>
				</PopoverContent>
			</Popover>

			<div className="flex items-center gap-2">
				<div className="relative flex-1">
					<Input
						ref={timeInputRef}
						id={timeInputId}
						type="time"
						step="60"
						defaultValue={normalizedTimePart || normalizedDefaultTime}
						onChange={handleTimeChange}
						disabled={disabled || !parts.datePart}
						className="pr-10"
					/>
					<Clock className="pointer-events-none absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
				</div>
				<Label htmlFor={timeInputId} className="text-sm text-muted-foreground whitespace-nowrap">
					{timeLabel}
				</Label>
			</div>
		</div>
	);
}
